import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/invitations/${token}`)
      .then(r => setInvite(r.data))
      .catch(e => setError(formatApiErrorDetail(e.response?.data?.detail) || "Invalid invitation"));
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/accept-invite", { token, password });
      await refresh();
      toast.success("Welcome to the studio");
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (error && !invite) {
    return (
      <div className="min-h-screen grid place-items-center bg-white">
        <div className="max-w-sm text-center">
          <div className="font-display text-2xl font-bold mb-2">Invitation unavailable</div>
          <div className="text-sm text-neutral-500 mb-6">{error}</div>
          <Button onClick={() => navigate("/login")} className="rounded-sm bg-neutral-900 hover:bg-neutral-800">Go to sign in</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="accept-invite-page">
      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-10">
            <div className="w-8 h-8 bg-neutral-900 grid place-items-center"><Sparkles size={16} className="text-white" /></div>
            <div className="font-display font-bold tracking-tight text-lg">STUDIO/OS</div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-3">Activate your seat</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mb-2">
            {invite ? `Hello, ${invite.name?.split(" ")[0] || "there"}.` : "Loading…"}
          </h1>
          <p className="text-sm text-neutral-500 mb-8">
            {invite && <>You've been invited as a <span className="font-semibold text-neutral-900">{invite.role}</span>. Set your password to get started.</>}
          </p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="accept-invite-form">
            <div>
              <Label className="text-xs uppercase tracking-widest font-semibold text-neutral-500">Email</Label>
              <Input value={invite?.email || ""} readOnly className="mt-1 rounded-sm bg-neutral-50" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-semibold text-neutral-500">Choose a password</Label>
              <Input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6}
                className="mt-1 rounded-sm" data-testid="invite-password-input" />
            </div>
            {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-sm" data-testid="invite-error">{error}</div>}
            <Button type="submit" disabled={loading || !invite} className="w-full rounded-sm bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="invite-submit">
              {loading ? "Activating…" : "Accept & sign in"}
            </Button>
          </form>
        </div>
      </div>
      <div className="hidden lg:flex flex-1 bg-neutral-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="relative z-10 flex flex-col justify-center p-12 w-full">
          <div className="font-display text-5xl font-bold tracking-tight leading-[1] mb-6">Welcome.</div>
          <div className="text-sm text-neutral-400 max-w-md">You're being added to a working studio. After activating, you'll see the projects assigned to you and can start logging time straight away.</div>
        </div>
      </div>
    </div>
  );
}
